import React from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, AlertTriangle, Info, Mail } from "lucide-react";

export default function LegalNoticePage() {
  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
        <p className="eyebrow mb-2">Legal · Protección al usuario</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A1A1A] leading-tight">
          Aviso legal y advertencia de fraude
        </h1>
        <p className="mt-4 text-lg text-[#475569] leading-relaxed">
          Lee con calma esta sección antes de contactar con cualquier anunciante.
          Tu seguridad depende también de las precauciones que tomes tú.
        </p>

        {/* Fraud warning callout */}
        <div className="mt-10 section-terracotta border border-[#A3B8DA] p-6 sm:p-8 relative overflow-hidden" data-testid="fraud-warning-box">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-white border border-[#A3B8DA]">
              <ShieldAlert className="w-5 h-5 text-[#1E3A8A]" />
            </div>
            <div className="space-y-3">
              <p className="eyebrow" style={{ color: "#1E3A8A" }}>Advertencia importante</p>
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1A1A1A]">
                Nunca compartas datos bancarios ni realices pagos a través de esta plataforma.
              </h2>
              <ul className="mt-2 space-y-2 text-sm text-[#1A1A1A] leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="text-[#1E3A8A] font-bold">•</span>
                  <span>
                    No facilites número de cuenta, tarjeta, PIN, contraseñas, ni códigos SMS por email,
                    teléfono o chat externo con ningún anunciante.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1E3A8A] font-bold">•</span>
                  <span>
                    Desconfía de anunciantes que exijan pagos por adelantado, transferencias urgentes o
                    señales para reservar sin verificación previa.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1E3A8A] font-bold">•</span>
                  <span>
                    Siempre que sea posible, verifica la identidad del anunciante en persona antes de
                    entregar cualquier cantidad económica.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1E3A8A] font-bold">•</span>
                  <span>
                    Si detectas un anuncio sospechoso, denúncialo escribiéndonos a{" "}
                    <a href="mailto:soporte@buscocompartir.com" className="link-underline font-semibold">
                      soporte@buscocompartir.com
                    </a>
                    .
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Legal sections */}
        <div className="mt-14 space-y-10">
          <Section n="01" title="Naturaleza del servicio">
            <p>
              <strong>buscocompartir</strong> (en adelante, “la Plataforma”) es un tablón de anuncios online
              dedicado exclusivamente a la publicación de anuncios de servicios para compartir por parte
              de terceros. La Plataforma actúa únicamente como intermediario tecnológico: <strong>no participa,
              ni interviene, ni supervisa</strong> las transacciones, acuerdos o comunicaciones que se establezcan
              entre anunciantes e interesados.
            </p>
          </Section>

          <Section n="02" title="Exención de responsabilidad por fraude">
            <p>
              La Plataforma <strong>no se hace responsable</strong> de los fraudes, estafas, engaños,
              incumplimientos o daños de cualquier tipo que pudieran derivarse de los anuncios publicados
              por terceros ni de las relaciones que los usuarios establezcan con dichos anunciantes.
              El usuario reconoce y acepta que asume, bajo su exclusiva responsabilidad, cualquier
              riesgo derivado del contacto y las operaciones que realice con otros usuarios de la Plataforma.
            </p>
            <p className="mt-3">
              En particular, la Plataforma advierte expresamente que <strong>no se deben proporcionar
              datos bancarios, contraseñas, códigos de verificación ni realizar pagos a través de este
              medio</strong>. Cualquier operación económica se hace fuera del ámbito de la Plataforma y bajo
              responsabilidad de las partes intervinientes.
            </p>
          </Section>

          <Section n="03" title="Moderación y retirada de contenidos">
            <p>
              La Plataforma se reserva el derecho de retirar cualquier anuncio que considere fraudulento,
              engañoso, ilegal, ofensivo o contrario a estos términos, sin necesidad de aviso previo y
              sin que ello genere derecho alguno a indemnización a favor del anunciante.
            </p>
          </Section>

          <Section n="04" title="Datos de contacto entre usuarios">
            <p>
              Los datos de contacto que un anunciante muestra en su anuncio son proporcionados por él
              mismo y bajo su exclusiva responsabilidad. La Plataforma no verifica su autenticidad. El
              interesado utiliza esos datos para contactar directamente con el anunciante, sin
              intervención de la Plataforma.
            </p>
          </Section>

          <Section n="05" title="Protección de datos">
            <p>
              La Plataforma trata los datos personales de los usuarios conforme al Reglamento General de
              Protección de Datos (UE) 2016/679 y a la Ley Orgánica 3/2018, de Protección de Datos y
              Garantía de los Derechos Digitales. El usuario puede ejercer sus derechos de acceso,
              rectificación, supresión, oposición, limitación y portabilidad escribiendo a{" "}
              <a href="mailto:privacidad@buscocompartir.com" className="link-underline font-semibold text-[#3B5FB8]">
                privacidad@buscocompartir.com
              </a>
              .
            </p>
          </Section>

          <Section n="06" title="Jurisdicción y ley aplicable">
            <p>
              Las presentes condiciones se rigen por la legislación española. Para cualquier controversia
              relativa al uso de la Plataforma, las partes se someten a los Juzgados y Tribunales del
              domicilio del usuario cuando este actúe como consumidor.
            </p>
          </Section>
        </div>

        {/* Contact block */}
        <div className="mt-14 border border-[#E2E8F0] bg-white p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Info className="w-6 h-6 text-[#3B5FB8] flex-shrink-0" />
          <div className="text-sm text-[#475569]">
            <p>
              Si tienes dudas sobre este aviso legal o quieres reportar un anuncio, escríbenos a{" "}
              <a href="mailto:soporte@buscocompartir.com" className="link-underline font-semibold text-[#1A1A1A]">
                soporte@buscocompartir.com
              </a>
              .
            </p>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-[#3B5FB8] link-underline"
          >
            ← Volver a la portada
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <div className="border-l-2 border-[#3B5FB8] pl-6">
      <p className="font-mono text-[#3B5FB8] mb-2 tracking-widest text-sm">{n}</p>
      <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1A1A1A] mb-3">{title}</h3>
      <div className="text-[#475569] leading-relaxed text-[15px] space-y-3">{children}</div>
    </div>
  );
}
